import {
  EVALUATOR_TYPE_LABELS,
  type EvaluatorType,
  type FollowUpPhaseResponse,
  type FollowUpSheetResponse,
  type FollowUpSkillItemResponse,
} from '@lms/shared'
import { AlertCircle, Calendar, MessageSquare, Target } from 'lucide-react'
import { useState } from 'react'
import { ReturnEvaluationDialog } from '@/components/training/ReturnEvaluationDialog'
import { SkillCheckEvaluationForm } from '@/components/training/SkillCheckEvaluationForm'
import { SkillCheckScoreTable } from '@/components/training/SkillCheckScoreTable'
import { StatusBadge } from '@/components/training/StatusBadge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'

/** スキルチェックシート「ガイドライン」の評価者ごとの説明 */
const EVALUATION_DESCRIPTIONS: Record<EvaluatorType, string> = {
  self: 'スキルチェック項目に関して、普段の業務でどのくらいできているか評価してください。',
  supervisor:
    'スキルチェック項目に関して、普段の業務で受講者本人がどのくらいできているか評価してください。',
}

const formatDate = (value: string | Date) =>
  new Date(value).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })

/** 成功した場合のみ true を返すハンドラ */
type Handler<Args extends unknown[]> = (...args: Args) => Promise<boolean>

interface FollowUpPhaseCardProps {
  phase: FollowUpPhaseResponse
  skillItems: FollowUpSkillItemResponse[]
  viewer: FollowUpSheetResponse['viewer']
  onSubmitEvaluation: Handler<
    [
      followUpId: string,
      evaluatorType: EvaluatorType,
      scores: { itemCode: string; score: number }[],
    ]
  >
  onReturnEvaluation: Handler<[followUpId: string, evaluatorType: EvaluatorType, reason: string]>
  onAddComment: Handler<[followUpId: string, content: string]>
  isSubmitting: boolean
  isReturning: boolean
  isCommenting: boolean
}

export function FollowUpPhaseCard({
  phase,
  skillItems,
  viewer,
  onSubmitEvaluation,
  onReturnEvaluation,
  onAddComment,
  isSubmitting,
  isReturning,
  isCommenting,
}: FollowUpPhaseCardProps) {
  const [commentText, setCommentText] = useState('')
  const { self, supervisor } = phase.evaluations
  const { actions } = phase

  const notOpenYet = new Date() < new Date(phase.availableFrom)
  const hasEvaluation = self !== null || supervisor !== null
  // 自分が再提出する評価の差し戻し理由だけを表示する
  const returnedForViewer = [
    actions.canSubmitSelf && self?.status === 'returned' ? self : null,
    actions.canSubmitSupervisor && supervisor?.status === 'returned' ? supervisor : null,
  ].filter((e) => e !== null)

  const evaluatorToSubmit: EvaluatorType | null = actions.canSubmitSelf
    ? 'self'
    : actions.canSubmitSupervisor
      ? 'supervisor'
      : null

  const handleComment = async () => {
    if (await onAddComment(phase.id, commentText)) setCommentText('')
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{phase.label}</CardTitle>
          <StatusBadge type="followUp" status={phase.status} />
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Calendar className="h-3.5 w-3.5" />
          <span>
            回答開始: {formatDate(phase.availableFrom)} ／ 提出期日: {formatDate(phase.dueDate)}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {notOpenYet && (
          <p className="text-sm text-muted-foreground">
            {formatDate(phase.availableFrom)} から評価できます。
          </p>
        )}

        {returnedForViewer.map((evaluation) => (
          <Alert key={evaluation.evaluatorType} variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {EVALUATOR_TYPE_LABELS[evaluation.evaluatorType]}評価が差し戻されました。理由:{' '}
              {evaluation.returnReason}
            </AlertDescription>
          </Alert>
        ))}

        {hasEvaluation && (
          <SkillCheckScoreTable skillItems={skillItems} evaluations={phase.evaluations} />
        )}

        {evaluatorToSubmit && (
          <SkillCheckEvaluationForm
            title={`${EVALUATOR_TYPE_LABELS[evaluatorToSubmit]}評価`}
            description={EVALUATION_DESCRIPTIONS[evaluatorToSubmit]}
            skillItems={skillItems}
            isSubmitting={isSubmitting}
            onSubmit={(scores) => onSubmitEvaluation(phase.id, evaluatorToSubmit, scores)}
          />
        )}

        {viewer.isTrainee && self?.status === 'submitted' && !supervisor && (
          <p className="text-sm text-muted-foreground">上司評価をお待ちください。</p>
        )}
        {viewer.isSupervisor && !notOpenYet && self?.status !== 'submitted' && (
          <p className="text-sm text-muted-foreground">本人評価の提出後に上司評価を行えます。</p>
        )}

        {phase.phase === 1 &&
          phase.status === 'completed' &&
          (viewer.isTrainee || viewer.isSupervisor) && (
            <Alert>
              <Target className="h-4 w-4" />
              <AlertDescription>
                評価が終了しました。研修で設定した行動目標について上司と確認してください。
              </AlertDescription>
            </Alert>
          )}

        {(actions.canReturnSelf || actions.canReturnSupervisor) && (
          <div className="flex flex-wrap gap-2">
            {(['self', 'supervisor'] as const)
              .filter((type) =>
                type === 'self' ? actions.canReturnSelf : actions.canReturnSupervisor,
              )
              .map((type) => (
                <ReturnEvaluationDialog
                  key={type}
                  evaluationLabel={`${EVALUATOR_TYPE_LABELS[type]}評価`}
                  isPending={isReturning}
                  onConfirm={(reason) => onReturnEvaluation(phase.id, type, reason)}
                />
              ))}
          </div>
        )}

        {phase.comments.length > 0 && (
          <div className="space-y-2">
            <h4 className="flex items-center gap-1 text-sm font-medium">
              <MessageSquare className="h-3.5 w-3.5" />
              コメント ({phase.comments.length})
            </h4>
            {phase.comments.map((c) => (
              <div key={c.id} className="border-l-2 border-muted py-1 pl-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium">{c.userName}</span>
                  {c.isSupervisor && <span className="font-medium text-emerald-600">上司</span>}
                  <span>{formatDate(c.createdAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{c.content}</p>
              </div>
            ))}
          </div>
        )}

        {actions.canComment && (
          <div className="space-y-2">
            <Textarea
              placeholder="コメント（任意）"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={2}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleComment}
              disabled={!commentText.trim() || isCommenting}
            >
              {isCommenting ? '送信中...' : 'コメント'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
